import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, RadialGauge, Pill, AiCard } from "@/components/perf";
import { Editable } from "@/context/ContentContext";
import PageBackdrop from "@/components/PageBackdrop";
import VideoAnalyzer from "@/components/VideoAnalyzer";
import FilmPsiCompare from "@/components/FilmPsiCompare";
import DrillScoreTrends from "@/components/DrillScoreTrends";
import RecGames from "@/components/RecGames";
import { toast } from "sonner";
import { Target, Sparkles, Activity, ChevronRight, HelpCircle, ChevronDown, Clapperboard, BrickWall, CheckCircle2 } from "lucide-react";

const CATEGORIES = {
  dinks: { label: "Dinks", desc: "Soft game control", color: "#4ADE80" },
  drops: { label: "Drops", desc: "Transition setup", color: "#16A34A" },
  drives: { label: "Drives", desc: "Pace generation", color: "#A3E635" },
  resets: { label: "Resets", desc: "Defensive stabilization", color: "#10B981" },
  wall: { label: "Wall", desc: "Solo wall training", color: "#C084FC" },
};

const LEVELS = [
  { key: "Low Intermediate", label: "Low Intermediate", phase: "Foundation Phase", objective: "Build control, consistency, and mechanics", color: "#22C55E" },
  { key: "Intermediate", label: "Intermediate", phase: "Development Phase", objective: "Add decision-making and control under pressure", color: "#EAB308" },
  { key: "High Intermediate", label: "High Intermediate", phase: "Refinement Phase", objective: "Sharpen discipline, recovery, and directional control", color: "#F97316" },
  { key: "Advanced", label: "Advanced", phase: "Performance Phase", objective: "Execute under speed, pressure, and strategy", color: "#EF4444" },
  { key: "Pro", label: "Pro", phase: "Mastery Phase", objective: "Deception, reaction speed, and execution under stress", color: "#A855F7" },
];

const WALL_LEVELS = [
  { key: "Low Intermediate", color: "#22C55E" },
  { key: "Intermediate", color: "#EAB308" },
  { key: "High Intermediate", color: "#F97316" },
  { key: "Advanced", color: "#EF4444" },
  { key: "Pro", color: "#A855F7" },
];

const DETAIL_ROWS = [
  ["setup", "Setup"], ["execution", "Execution"], ["constraints", "Constraints"],
  ["focus_points", "Focus Points"], ["scoring", "Scoring"], ["purpose", "Purpose"],
];

export default function Training() {
  const { refresh } = useAuth();
  const [drills, setDrills] = useState([]);
  const [pm, setPm] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [rec, setRec] = useState(null);
  const [recLoading, setRecLoading] = useState(true);
  const [tipsBusy, setTipsBusy] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [openLevels, setOpenLevels] = useState({ "Low Intermediate": true });
  const [completedIds, setCompletedIds] = useState([]);
  const [active, setActive] = useState(null);
  const [reps, setReps] = useState(20);
  const [made, setMade] = useState(15);
  const [repPresets, setRepPresets] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [d, me, ch, ss, va, rp] = await Promise.all([
      api.get("/lda/drills"), api.get("/auth/me"), api.get("/lda/challenge"), api.get("/lda/sessions"), api.get("/videos/analyses"), api.get("/lda/rep-presets"),
    ]);
    setDrills(d.data); setPm(me.data.player_model); setChallenge(ch.data); setSessions(ss.data); setAnalyses(va.data); setRepPresets(rp.data);
    setCompletedIds(me.data.drills_completed || []);
  };

  const loadRec = async () => {
    setRecLoading(true);
    try { const { data } = await api.get("/lda/recommendation"); setRec(data); }
    catch { /* ignore */ } finally { setRecLoading(false); }
  };

  const genTips = async () => {
    setTipsBusy(true);
    try {
      const { data } = await api.get("/lda/recommendation", { params: rec?.advice ? { force: true } : { generate: true } });
      setRec(data);
      if (data.coins_charged > 0) toast.success(`Fresh AI tip — ${data.coins_charged} BLC`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't generate tips"); }
    finally { setTipsBusy(false); }
  };

  useEffect(() => { load(); loadRec(); }, []);

  const submitLog = async () => {
    if (!active) return toast.error("Select a drill first");
    setBusy(true);
    try {
      const { data } = await api.post("/lda/sessions", {
        category: active.category, drill_id: active.id, reps: Number(reps), made: Number(made), difficulty: active.level.toLowerCase(),
      });
      setPm(data.player_model);
      toast.success(`Logged ${reps} reps · ${data.session.score}% accuracy`);
      await Promise.all([load(), loadRec(), refresh()]);
      setActive(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const renderDrill = (d, color) => {
    const c = CATEGORIES[d.category];
    const open = expanded === d.id;
    return (
      <div key={d.id} data-testid={`drill-${d.id}`} className={`bg-pb-surface border transition-all ${open ? "border-pb-red" : "border-pb-border"}`}>
        <div className="flex items-center gap-3 p-4">
          <span className="font-display font-black text-xl w-6 text-center" style={{ color }}>{d.order}</span>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg uppercase leading-tight truncate">{d.name}</div>
            <div className="text-xs text-zinc-500 mt-0.5"><Pill color={c.color}>{c.label}</Pill></div>
          </div>
          <button data-testid={`drill-info-${d.id}`} aria-label="Drill details"
            onClick={() => setExpanded(open ? null : d.id)}
            className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 transition-all ${open ? "bg-pb-red border-pb-red text-black" : "border-pb-border text-zinc-400 hover:border-pb-red hover:text-pb-red"}`}>
            {open ? <ChevronDown className="w-4 h-4" /> : <HelpCircle className="w-4 h-4" />}
          </button>
          <button data-testid={`drill-log-${d.id}`}
            onClick={() => { const preset = repPresets?.[d.level] || d.target_reps || 100; setActive(d); setReps(preset); setMade(Math.round(preset * 0.7)); }}
            className="border border-pb-cyan/50 text-pb-cyan font-bold uppercase tracking-widest text-[10px] px-3 py-2 hover:bg-pb-cyan hover:text-black active:scale-95 transition-all shrink-0">
            Log
          </button>
          {completedIds.includes(d.id) ? (
            <span data-testid={`drill-passed-${d.id}`} className="flex items-center gap-1 border border-[#A3E635]/60 bg-[#A3E635]/10 text-[#A3E635] font-bold uppercase tracking-widest text-[10px] px-2.5 py-1.5 shrink-0">
              <CheckCircle2 className="w-3.5 h-3.5" /> Passed
            </span>
          ) : (
            <button data-testid={`drill-submit-${d.id}`} onClick={() => setExpanded(open ? null : d.id)}
              className="bg-pb-red text-black font-bold uppercase tracking-widest text-[10px] px-3 py-2 hover:bg-[#BEF264] active:scale-95 transition-all shrink-0">
              Submit Video
            </button>
          )}
        </div>
        {open && (
          <div className="px-4 pb-4 pt-1 border-t border-pb-border/60 animate-fade-up" data-testid={`drill-detail-${d.id}`}>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mt-3">Drill Information — Required Execution Standards</div>
            <dl className="space-y-2 mt-2">
              {DETAIL_ROWS.map(([k, label]) => (
                <div key={k} className="grid grid-cols-[110px_1fr] gap-2 text-sm">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.15em] text-pb-cyan pt-0.5">{label}</dt>
                  <dd className="text-zinc-300">{d[k]}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-4 border-t border-pb-border/60 pt-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-pb-cyan mb-1 flex items-center gap-1">
                <Clapperboard className="w-3 h-3" /> Submit Your Drill Video — upload or live-record
              </div>
              <p className="text-[11px] text-zinc-500 mb-2" data-testid={`drill-pass-standard-${d.id}`}>
                The AI grades your execution 0–100 against the standards above. <b className="text-zinc-300">Passing standard: {({"Low Intermediate":60,Intermediate:65,"High Intermediate":70,Advanced:75,Pro:80,Wall:65})[d.level] || 70}/100</b>{d.level !== "Wall" ? " — pass it and the drill counts toward your PSI. Resubmit as many times as you need." : ""}
              </p>
              <VideoAnalyzer kind="drill" drill={d} compact onComplete={load} />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <PageBackdrop ck="backdrop.training" image="https://static.prod-images.emergentagent.com/jobs/ccb7baf7-4fe3-4351-b3c9-d8e5a28ac676/images/7c72aa18fb798e2290fcc592d434ecf93bf3d99fc427f3cff5a18b92256a8349.png" />
      <div className="relative z-10">
      <div className="flex items-center gap-4 sm:gap-6 mb-8 animate-fade-up">
        <img src="https://customer-assets.emergentagent.com/job_pickle-os/artifacts/kb84zhqr_Background%20Eraser.png"
          alt="Let's Drill About It" data-testid="lda-logo"
          className="h-24 sm:h-32 lg:h-40 w-auto object-contain shrink-0 drop-shadow-[0_0_30px_rgba(249,115,22,0.25)]" />
        <div>
          <Editable ck="page.training.kicker" as="div" fallback="Foundation Layer · Let's Drill About It" className="text-xs font-bold uppercase tracking-[0.3em] text-pb-cyan mb-2" />
          <Editable ck="page.training.title" as="h1" fallback="LDA Training" className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none" />
          <Editable ck="page.training.subtitle" as="p" fallback="Structured repetition builds measurable skill. Every rep feeds your live Player Skill Index and competitive readiness." className="text-zinc-400 mt-3 max-w-2xl text-sm md:text-base" />
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-pb-surface border border-pb-border p-5 flex items-center gap-4 col-span-2 sm:col-span-1 min-w-0">
          <RadialGauge value={pm?.skill_index ?? 0} label="PSI" color="#A3E635" size={110} />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Player Skill Index</div>
            <div className="font-display font-black text-xl mt-1">{pm?.division || "—"}</div>
            <Pill color="#4ADE80">{pm?.rating || "Unrated"}</Pill>
          </div>
        </div>
        <StatCard testid="stat-total-reps" label="Total Reps" value={pm?.total_reps ?? 0} accent="#4ADE80" />
        <StatCard testid="stat-efficiency" label="Drill Efficiency" value={pm?.drill_efficiency ?? 0} unit="%" accent="#10B981" />
        <StatCard testid="stat-sessions" label="Sessions" value={pm?.sessions_count ?? 0} accent="#A3E635" />
      </div>

      {pm?.film_comparison && <div className="-mt-4 mb-8"><FilmPsiCompare pm={pm} compact /></div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: drills + logger */}
        <div className="lg:col-span-2 space-y-6">
          {/* 1M Rep Challenge */}
          {challenge && (
            <div className="bg-pb-surface border border-pb-border p-6" data-testid="rep-challenge">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-pb-red" />
                  <span className="font-display font-bold text-lg uppercase">1 Billion Rep Challenge</span>
                </div>
                <span className="font-mono text-pb-cyan text-sm">{challenge.percent}%</span>
              </div>
              <div className="h-3 bg-pb-elevated relative overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-pb-red to-pb-cyan" style={{ width: `${Math.max(1, challenge.percent)}%` }} />
              </div>
              <div className="flex justify-between mt-2 text-xs text-zinc-500 font-mono">
                <span>{challenge.total_reps.toLocaleString()} global reps</span>
                <span>You: {challenge.my_reps.toLocaleString()} / Goal 1,000,000,000</span>
              </div>
            </div>
          )}

          {/* Game Film — AI Video Analysis */}
          <div className="bg-pb-surface border border-pb-border p-6" data-testid="game-film-section">
            <div className="flex items-center gap-2 mb-1">
              <Clapperboard className="w-5 h-5 text-pb-red" />
              <h3 className="font-display font-bold text-xl uppercase">Game Film · AI Video Analysis</h3>
            </div>
            <p className="text-sm text-zinc-500 mb-4">Upload or live-record your games and drills — first take sets your baseline. The AI grades you like a pro scout against the most fundamentally sound, most consistent player possible: every fundamental broken down (ready position, footwork, soft game, power game, serve & return, transition zone, shot selection, consistency). Every score feeds your Player Skill Index.</p>
            <VideoAnalyzer kind="gameplay" onComplete={load} />
            {analyses.length > 0 && (
              <div className="mt-5 border-t border-pb-border/60 pt-4 space-y-3" data-testid="video-history">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Recent AI Reviews</div>
                {analyses.slice(0, 5).map((a) => (
                  <div key={a.id} data-testid={`video-review-${a.id}`} className="flex gap-3 items-start text-sm">
                    <span className="font-display font-black text-xl text-pb-red w-14 shrink-0">
                      {a.status === "complete" ? a.score : "…"}<span className="text-[10px] text-zinc-600">/100</span>
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-widest text-pb-cyan">{a.kind === "drill" ? a.drill_name : "Gameplay"}</div>
                      {a.strengths && <p className="text-xs text-green-400/80 line-clamp-1">✓ {a.strengths}</p>}
                      <p className="text-xs text-zinc-400 line-clamp-2">{a.feedback || (a.status === "processing" ? "Analyzing…" : a.error || "Failed")}</p>
                      {a.fundamentals && (
                        <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`fundamentals-${a.id}`}>
                          {Object.entries(a.fundamentals).map(([k, v]) => (
                            <span key={k} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-pb-border/60 text-zinc-400">
                              {k.replace(/_/g, " ")} <span className="font-bold" style={{ color: v >= 70 ? "#4ADE80" : v >= 50 ? "#FACC15" : "#F87171" }}>{v}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <RecGames onModel={setPm} />

          <div>
            <h3 className="font-display font-bold text-xl uppercase mb-4 flex items-center gap-2"><Target className="w-5 h-5 text-pb-red" /> Drill Library</h3>
            <div className="space-y-4">
              {LEVELS.map((lvl) => {
                const slug = lvl.key.toLowerCase().replace(/\s+/g, "-");
                const lvlDrills = drills.filter((d) => d.level === lvl.key);
                const openLvl = !!openLevels[lvl.key];
                return (
                  <div key={lvl.key} className="border border-pb-border/60 bg-pb-surface/30">
                    <button data-testid={`level-toggle-${slug}`} onClick={() => setOpenLevels((o) => ({ ...o, [lvl.key]: !o[lvl.key] }))}
                      className="w-full flex items-center gap-3 border-l-4 pl-3 pr-4 py-3 text-left hover:bg-pb-elevated/40 transition-colors" style={{ borderColor: lvl.color }}>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-black text-lg uppercase leading-none" style={{ color: lvl.color }}>{lvl.label} <span className="text-zinc-500 text-sm">· {lvl.phase}</span></div>
                        <div className="text-xs text-zinc-500 mt-1">Objective: {lvl.objective}</div>
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-600 shrink-0">{lvlDrills.length} drills</span>
                      <ChevronDown className={`w-5 h-5 text-zinc-500 shrink-0 transition-transform duration-200 ${openLvl ? "rotate-180" : ""}`} />
                    </button>
                    {openLvl && (
                      <div className="space-y-3 p-3 pt-1 animate-fade-up">
                        {lvlDrills.map((d) => renderDrill(d, lvl.color))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Wall Drill System — bonus section */}
              <div data-testid="wall-drill-section"
                className="border border-[#C084FC]/60 bg-gradient-to-br from-[#C084FC]/10 via-pb-surface/40 to-pb-surface/30 shadow-[0_0_24px_rgba(192,132,252,0.10)]">
                <button data-testid="level-toggle-wall" onClick={() => setOpenLevels((o) => ({ ...o, Wall: !o.Wall }))}
                  className="w-full flex items-center gap-3 border-l-4 border-[#C084FC] pl-3 pr-4 py-3 text-left hover:bg-[#C084FC]/10 transition-colors">
                  <BrickWall className="w-6 h-6 text-[#C084FC] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-black text-lg uppercase leading-none text-[#C084FC]">
                      Wall Drill System <span className="ml-2 bg-[#C084FC] text-black text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 align-middle">Bonus · Feeds Your PSI</span>
                    </div>
                    <div className="text-xs text-zinc-400 mt-1">Solo wall training — builds decision-making under pressure, recovery and balance, deception, and consistency at speed. Every logged wall rep adds a bonus to your PSI.</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">{drills.filter((d) => d.level === "Wall").length} drills</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 shrink-0 transition-transform duration-200 ${openLevels.Wall ? "rotate-180" : ""}`} />
                </button>
                {openLevels.Wall && (
                  <div className="p-3 pt-1 animate-fade-up">
                    {WALL_LEVELS.map((wl) => {
                      const wDrills = drills.filter((d) => d.level === "Wall" && d.wall_level === wl.key);
                      if (!wDrills.length) return null;
                      return (
                        <div key={wl.key} className="mb-4 last:mb-0">
                          <div className="text-[10px] font-black uppercase tracking-[0.2em] mb-2 pl-1" style={{ color: wl.color }}>{wl.key}</div>
                          <div className="space-y-3">{wDrills.map((d) => renderDrill(d, "#C084FC"))}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right: AI rec + breakdown + sessions */}
        <div className="space-y-6">
          <AiCard loading={recLoading}>
            {rec && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-pb-cyan" />
                  <span className="font-display font-bold uppercase">{rec.drill?.name}</span>
                </div>
                {rec.advice ? (
                  <p className="text-sm text-zinc-300 leading-relaxed" data-testid="lda-ai-advice">{rec.advice}</p>
                ) : (
                  <p className="text-sm text-zinc-500">Your next drill is picked from your real numbers. Tap below for a personalized AI coaching tip on it.</p>
                )}
                {rec.weakness && <div className="mt-3"><Pill color="#F59E0B">Weakness: {rec.weakness}</Pill></div>}
                <button data-testid="generate-ai-tips-btn" disabled={tipsBusy} onClick={genTips}
                  className="mt-3 flex items-center gap-1.5 border border-pb-cyan/60 text-pb-cyan hover:bg-pb-cyan hover:text-black px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition-colors disabled:opacity-50">
                  <Sparkles className="w-3.5 h-3.5" /> {tipsBusy ? "Generating…" : rec.advice ? `Refresh AI Tips · ${rec.refresh_fee_blc ?? 5} BLC` : "Generate AI Tips — Free"}
                </button>
              </div>
            )}
          </AiCard>

          <div className="bg-pb-surface border border-pb-border p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">Category Accuracy</div>
            <div className="space-y-3">
              {Object.entries(CATEGORIES).map(([key, c]) => {
                const val = pm?.category_accuracy?.[key] ?? 0;
                return (
                  <div key={key}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-zinc-300">{c.label}</span>
                      <span className="font-mono" style={{ color: c.color }}>{val}%</span>
                    </div>
                    <div className="h-2 bg-pb-elevated"><div className="h-full" style={{ width: `${val}%`, backgroundColor: c.color }} /></div>
                  </div>
                );
              })}
            </div>
          </div>

          <DrillScoreTrends />

          <div className="bg-pb-surface border border-pb-border p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Recent Sessions</div>
            {sessions.length === 0 ? <div className="text-sm text-zinc-600">No sessions yet — submit a drill video to get graded.</div> :
              <div className="space-y-2">
                {sessions.slice(0, 6).map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-pb-border/50 pb-2">
                    <span className="text-zinc-300 capitalize">{s.category}</span>
                    <span className="font-mono text-zinc-500">{s.made}/{s.reps}</span>
                    <span className="font-mono" style={{ color: s.score >= 70 ? "#10B981" : s.score >= 40 ? "#4ADE80" : "#A3E635" }}>{s.score}%</span>
                  </div>
                ))}
              </div>}
          </div>
        </div>
      </div>

      {/* Logger modal */}
      {active && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-pb-surface border border-pb-red w-full max-w-md p-6 animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="logger-modal">
            <div className="flex items-center gap-2">
              <Pill color={CATEGORIES[active.category].color}>{CATEGORIES[active.category].label}</Pill>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">{active.level}</span>
            </div>
            <h3 className="font-display font-black text-2xl uppercase mt-3">{active.name}</h3>
            <p className="text-sm text-zinc-500 mb-2">{active.focus_points}</p>
            <p className="text-[11px] text-zinc-600 mb-6">Rep logs track your training volume and feed the 1 Billion Rep Challenge — completing the drill for PSI still requires a passed AI video.</p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Reps Attempted</label>
                <input data-testid="input-reps" type="number" min="1" value={reps} onChange={(e) => setReps(e.target.value)}
                  className="w-full bg-pb-elevated border border-pb-border px-4 py-3 font-mono text-xl focus:outline-none focus:border-pb-red" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Reps Made</label>
                <input data-testid="input-made" type="number" min="0" value={made} onChange={(e) => setMade(e.target.value)}
                  className="w-full bg-pb-elevated border border-pb-border px-4 py-3 font-mono text-xl focus:outline-none focus:border-pb-cyan" />
              </div>
            </div>
            <button data-testid="submit-session" onClick={submitLog} disabled={busy}
              className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? "Logging…" : <>Log Session <ChevronRight className="w-4 h-4" /></>}
            </button>
          </div>
        </div>
      )}

      </div>
    </div>
  );
}
